import { Component, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { distinctUntilChanged, finalize, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { TeachCompensationService } from 'src/app/services/api/teach-compensation/teach-compensation.service';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import {
  MakeupTeachingRequestListParams,
  MakeupTeachingRequestModel,
} from 'src/app/services/models/teach-compensation/teach-compensation.model';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';

type TeachCompensationStatus = 'pending' | 'approved' | 'rejected' | 'revision_requested' | 'draft';

interface TeachCompensationRow {
  id: number;
  sequence?: number | string | null;
  academicTerm: string;
  year: string;
  leaveType: string;
  leaveDate: string;
  timeRange: string;
  subjectCode: string;
  subjectName: string;
  compensationDate: string;
  compensationTime: string;
  status: TeachCompensationStatus;
  statusLabel: string;
}

interface TeachCompensationDataSource {
  data: TeachCompensationRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

@Component({
  selector: 'app-teach-compensation',
  standalone: true,
  imports: [
    SharedModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './teach-compensation.component.html',
  styleUrl: './teach-compensation.component.scss',
})
export class TeachCompensationComponent implements OnInit {
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild('statusTemplate', { static: true })
  statusTemplate!: TemplateRef<any>;
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<any>;

  form!: FormGroup;
  columns: any[] = [];
  datasource = signal<TeachCompensationDataSource>(this.createDataSource([]));
  tableLoading = signal(false);
  pageSizeOptions = [10, 20, 50];
  academicTermOptions: string[] = [];
  yearOptions: string[] = [];

  private currentPage = 1;
  private currentLimit = 10;
  private currentSortParams: DatatableSortParams = { sort: null, direction: null };
  private readonly sortFieldMap: DatatableSortFieldMap = {
    index: 'sequence',
    leaveType: 'makeup_teaching_leave_type_id',
    leaveDate: 'leave_date',
    timeRange: 'makeup_teaching_leave_period_id',
    subjectCode: 'subject_code',
    subjectName: 'subject_name',
    compensationDate: 'makeup_teaching_date',
    compensationTime: 'makeup_start_time_id',
  };
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private authService = inject(AuthService);
  private studyPeriodService = inject(StudyPeriodService);
  private teachCompensationService = inject(TeachCompensationService);
  private academicTerms: AcademicTermYearModel[] = [];

  ngOnInit(): void {
    this.initForm();
    this.initColumns();
    this.listenFilters();
    this.loadAcademicTerms();
  }

  onCreateSubmission(): void {
    void this.router.navigate(['/teach-compensation-management/teach-compensation-details']);
  }

  onViewSubmission(row: TeachCompensationRow): void {
    void this.router.navigate([
      '/teach-compensation-management/teach-compensation-preview',
      row.id,
    ]);
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.loadSubmissions(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadSubmissions(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadSubmissions(1);
  }

  private initForm(): void {
    const defaultAcademicTerm = this.getDefaultAcademicTermName();
    const defaultYear = this.getDefaultBuddhistYear();

    this.academicTermOptions = [defaultAcademicTerm];
    this.yearOptions = [defaultYear];
    this.form = this.fb.group({
      academicTerm: [defaultAcademicTerm],
      year: [defaultYear],
    });
  }

  private listenFilters(): void {
    this.form.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => this.loadSubmissions(1));
  }

  private loadAcademicTerms(): void {
    this.studyPeriodService.getAcademicTermYears()
      .pipe(take(1))
      .subscribe({
        next: (terms) => {
          this.academicTerms = terms;
          const academicTermOptions = this.buildAcademicTermOptions(terms);
          const yearOptions = this.buildAcademicYearOptions(terms);

          this.academicTermOptions = academicTermOptions.length > 0 ? academicTermOptions : this.academicTermOptions;
          this.yearOptions = yearOptions.length > 0 ? yearOptions : this.yearOptions;
          this.ensureAcademicTermFilterValues();
          this.loadSubmissions(1);
        },
        error: (err) => {
          console.error(err);
          this.loadSubmissions(1);
        },
      });
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 72,
        minWidth: 72,
        maxWidth: 72,
      },
      {
        prop: 'leaveType',
        name: 'ประเภทการลา / หยุด',
        sortable: true,
        width: 150,
        minWidth: 150,
      },
      {
        prop: 'leaveDate',
        name: 'วันที่ลา / วันที่หยุด',
        sortable: true,
        width: 170,
        minWidth: 170,
        headerClass: 'teach-compensation-wrap-header',
        cellClass: 'teach-compensation-wrap-cell',
      },
      {
        prop: 'timeRange',
        name: 'ช่วงเวลาการลา',
        sortable: true,
        width: 180,
        minWidth: 180,
      },
      {
        prop: 'subjectCode',
        name: 'รหัสวิชา',
        sortable: true,
        width: 100,
        minWidth: 100,
      },
      {
        prop: 'subjectName',
        name: 'ชื่อรายวิชา',
        sortable: true,
        width: 190,
        minWidth: 190,
        headerClass: 'teach-compensation-wrap-header',
        cellClass: 'teach-compensation-wrap-cell',
      },
      {
        prop: 'compensationDate',
        name: 'วันที่ขอสอนชดเชย',
        sortable: true,
        width: 140,
        minWidth: 120,
      },
      {
        prop: 'compensationTime',
        name: 'เวลาสอนชดเชย',
        sortable: true,
        width: 120,
        minWidth: 120,
      },
      {
        prop: 'status',
        name: 'สถานะ:',
        sortable: false,
        cellTemplate: this.statusTemplate,
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        headerClass: 'text-center schedule-status-header',
        cellClass: 'text-center schedule-status-cell',
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        cellClass: 'text-center schedule-action-cell',
        headerClass: 'text-center schedule-action-header',
      },
    ];
  }

  private loadSubmissions(page = this.currentPage): void {
    this.currentPage = page;
    this.tableLoading.set(true);

    this.teachCompensationService.getMakeupTeachingRequestsList({
      ...this.buildAcademicTermFilterParams(),
      page,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => {
        const rows = response.data.map((item) => this.mapRequestToRow(item));

        this.datasource.set(this.createDataSource(
          rows,
          response.page,
          response.limit,
          response.total,
          response.total_pages,
          (response.page - 1) * response.limit,
        ));
      },
      error: (err) => {
        console.error(err);
        this.datasource.set(this.createDataSource([], page, this.currentLimit));
      },
    });
  }

  private createDataSource(
    rows: TeachCompensationRow[],
    page = 1,
    limit = 10,
    total = rows.length,
    totalPage = Math.max(Math.ceil(total / limit), 1),
    offset = (page - 1) * limit,
  ): TeachCompensationDataSource {
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      limit,
      offset: normalizedPage === page ? offset : (normalizedPage - 1) * limit,
      page: normalizedPage,
      total,
      total_page: totalPage,
    };
  }

  private findAcademicTerm(
    academicTermName: string,
    academicYear: string,
  ): AcademicTermYearModel | undefined {
    return this.academicTerms.find((term) => (
      String(term.name) === academicTermName &&
      String(this.toBuddhistYear(term.year)) === String(academicYear)
    ));
  }

  private buildAcademicTermOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => String(term.name).trim())
        .filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => this.toBuddhistYear(term.year))
        .filter((year) => Number.isFinite(year))
        .map((year) => String(year)),
    )).sort((a, b) => Number(b) - Number(a));
  }

  private ensureAcademicTermFilterValues(): void {
    const currentAcademicTerm = String(this.form.get('academicTerm')?.value ?? '');
    const currentYear = String(this.form.get('year')?.value ?? '');

    if (this.findAcademicTerm(currentAcademicTerm, currentYear) != null || this.academicTerms.length === 0) {
      return;
    }

    const fallbackTerm = this.academicTerms[0];

    this.form.patchValue({
      academicTerm: String(fallbackTerm.name),
      year: String(this.toBuddhistYear(fallbackTerm.year)),
    }, { emitEvent: false });
  }

  private getDefaultAcademicTermName(): string {
    const sessionAcademicTermName = String(this.authService.currentUser()?.academic_term?.name ?? '').trim();

    return sessionAcademicTermName === '' ? '1' : sessionAcademicTermName;
  }

  private getDefaultBuddhistYear(): string {
    const sessionYear = Number(this.authService.currentUser()?.academic_term?.year);

    if (!Number.isFinite(sessionYear) || sessionYear <= 0) {
      return String(this.toBuddhistYear(new Date().getFullYear()));
    }

    return String(this.toBuddhistYear(sessionYear));
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private toChristianYear(year: number): number {
    return year >= 2400 ? year - 543 : year;
  }

  private buildAcademicTermFilterParams(): Pick<
    MakeupTeachingRequestListParams,
    'academic_term_id' | 'academic_term_name' | 'academic_term_year'
  > {
    const { academicTerm, year } = this.form.getRawValue();
    const normalizedAcademicTerm = String(academicTerm ?? '').trim();
    const normalizedYear = String(year ?? '').trim();
    const selectedTerm = this.findAcademicTerm(normalizedAcademicTerm, normalizedYear);

    if (selectedTerm != null) {
      return { academic_term_id: selectedTerm.id };
    }

    const params: Pick<
      MakeupTeachingRequestListParams,
      'academic_term_name' | 'academic_term_year'
    > = {};

    if (normalizedAcademicTerm !== '') {
      params.academic_term_name = normalizedAcademicTerm;
    }

    const numericYear = Number(normalizedYear);

    if (Number.isFinite(numericYear) && numericYear > 0) {
      params.academic_term_year = this.toChristianYear(numericYear);
    }

    return params;
  }

  private mapRequestToRow(item: MakeupTeachingRequestModel): TeachCompensationRow {
    const academicTerm = this.findAcademicTermById(this.getAcademicTermId(item));
    const status = this.normalizeStatus(item.status);

    return {
      id: item.id,
      sequence: item.sequence,
      academicTerm: academicTerm?.name ?? '-',
      year: academicTerm != null ? String(this.toBuddhistYear(academicTerm.year)) : '-',
      leaveType: item.makeup_teaching_leave_type?.name ?? '-',
      leaveDate: this.formatLeaveDate(item),
      timeRange: this.formatLeavePeriod(item),
      subjectCode: this.getSubjectCode(item),
      subjectName: this.getSubjectName(item),
      compensationDate: this.formatDate(item.makeup_teaching_date),
      compensationTime: this.formatTimeRange(
        item.makeup_start_time?.label ?? item.makeup_start_time?.time_value,
        item.makeup_end_time?.label ?? item.makeup_end_time?.time_value,
      ),
      status,
      statusLabel: this.getStatusLabel(status),
    };
  }

  private getAcademicTermId(item: MakeupTeachingRequestModel): number | null {
    return item.schedule_submission?.academic_term_id ??
      item.academic_holiday?.academic_term_id ??
      item.subject_schedule_group?.academic_term_id ??
      null;
  }

  private findAcademicTermById(id: number | null): AcademicTermYearModel | undefined {
    if (id == null) {
      return undefined;
    }

    return this.academicTerms.find((term) => term.id === id);
  }

  private formatLeaveDate(item: MakeupTeachingRequestModel): string {
    const holidayName = item.academic_holiday?.name;
    const holidayDate = this.formatDate(item.academic_holiday?.holiday_date);

    if (holidayName != null && holidayName !== '' && holidayDate !== '-') {
      return `${holidayName} (${holidayDate})`;
    }

    if (holidayDate !== '-') {
      return holidayDate;
    }

    if (holidayName != null && holidayName !== '') {
      return holidayName;
    }

    return this.formatDate(item.leave_date);
  }

  private formatLeavePeriod(item: MakeupTeachingRequestModel): string {
    const period = item.makeup_teaching_leave_period;
    const periodName = period?.name ?? period?.label;
    const startTime = this.formatTime(period?.start_time);
    const endTime = this.formatTime(period?.end_time);

    if (periodName != null && periodName !== '' && startTime !== '-' && endTime !== '-') {
      return `${periodName} (${startTime} - ${endTime} น.)`;
    }

    return periodName ?? '-';
  }

  private formatTimeRange(start?: string | null, end?: string | null): string {
    const startTime = this.formatTime(start);
    const endTime = this.formatTime(end);

    if (startTime === '-' || endTime === '-') {
      return '-';
    }

    return `${startTime} - ${endTime} น.`;
  }

  private formatDate(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = this.toBuddhistYear(date.getFullYear());

    return `${day}/${month}/${year}`;
  }

  private formatTime(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const timeMatch = /T(\d{2}):(\d{2})/.exec(value);

    if (timeMatch != null) {
      return `${Number(timeMatch[1])}:${timeMatch[2]}`;
    }

    const timeParts = value.split(':');

    if (timeParts.length < 2) {
      return value;
    }

    const [hours, minutes] = timeParts;

    return `${Number(hours)}:${minutes}`;
  }

  private getSubjectCode(item: MakeupTeachingRequestModel): string {
    return item.subject?.code ??
      item.subject_schedule_group?.subject?.code ??
      item.subject_schedule_group?.code ??
      '-';
  }

  private getSubjectName(item: MakeupTeachingRequestModel): string {
    return item.subject?.name ??
      item.subject_schedule_group?.subject?.name ??
      item.subject_schedule_group?.name ??
      '-';
  }

  private normalizeStatus(status?: string | null): TeachCompensationStatus {
    if (status === 'approved' || status === 'rejected' || status === 'revision_requested' || status === 'draft') {
      return status;
    }

    return 'pending';
  }

  private getStatusLabel(status: TeachCompensationStatus): string {
    const labels: Record<TeachCompensationStatus, string> = {
      pending: 'รอดำเนินการ',
      approved: 'รับรองแล้ว',
      rejected: 'ไม่รับรอง',
      revision_requested: 'ส่งกลับแก้ไข',
      draft: 'รอดำเนินการ',
    };

    return labels[status];
  }

}
